<script lang="ts">
	import { Route, Router } from 'svelte5-router'
	import { t } from '$i18n/i18n'
	import { RequestedEntityType } from '$lib/bid/types/requestDto'
	import VehicleCategoriesLoader from '$lib/atoms/VehicleCategoriesLoader.svelte'
	import { MaterialView } from '$lib/mmpv/types/materialView'
	import { pageError } from '$lib/stores'

	import { onMount } from 'svelte'
	import AnalyticalPage from './lib/analytics/AnalyticalPage.svelte'
	import CorporateDesignPage from './lib/corporateDesign/CorporateDesignPage.svelte'
	import Dashboard from './lib/dashboard/Dashboard.svelte'
	import HelpConfiguration from './lib/help/configuration/HelpConfiguration.svelte'
	import HelpBox from './lib/help/HelpBox.svelte'
	import InstallationCasesPage from './lib/ic/InstallationCasesPage.svelte'
	import MailProtocolPage from './lib/mail/MailProtocolPage.svelte'
	import MailTemplateConfigPage from './lib/mail/templates/MailTemplateConfigPage.svelte'
	import MapView from './lib/map/MapView.svelte'
	import MaterialCatalogPageManager from './lib/mmpv/MaterialCatalogPageManager.svelte'
	import FooterMM from './lib/molecules/FooterMM.svelte'
	import OptimizerPage from './lib/optimizer/OptimizerPage.svelte'
	import OptimizerPageSaas from './lib/optimizer/saas/OptimizerPageSaas.svelte'
	// import LoadingAnimations from './lib/organisms/LoadingAnimations.svelte'
	import LotAccountingMailConfigurationPage from './lib/pages/LotAccountingMailConfigurationPage.svelte'
	import MatchCheck from './lib/pages/MatchCheck.svelte'
	import NestedSiriusIFramePage from './lib/pages/NestedSiriusIFramePage.svelte'
	import AiDashboardSaasPage from './lib/pages/AiDashboardSaasPage.svelte'
	import OffererPubPage from './lib/pages/OffererPubPage.svelte'
	import OptimizerPubPageFinished from './lib/pages/OptimizerPubPageFinished.svelte'
	import PubPage404 from './lib/pages/PubPage404.svelte'
	import PubPage40x from './lib/pages/PubPage40x.svelte'
	import DefaultLandingPage from './lib/utils/DefaultLandingPage.svelte'
	import { loadHotjar } from '$utils/hotjar'
	import MetaBroker from './lib/badger/MetaBroker.svelte'
	import TagsConfigurationPage from '$lib/manage/tags/TagsConfigurationPage.svelte'

	// Used for SSR. A falsy value is ignored by the Router.
	// "SSR" means server side rendering, for everyone, like me, who didn't know yet
	let { url = '' }: { url?: string } = $props()

	const currentError = $derived($pageError)

	const production = import.meta.env.MODE === 'production'

	onMount(async () => {
		const PUB_PAGES_URLS_FOR_HOTJAR = ['/position/']
		await loadHotjar(PUB_PAGES_URLS_FOR_HOTJAR.some((s) => window.location.href.includes(s)))
	})
</script>

{#if currentError}
	{#if currentError.code === 401}
		<PubPage40x description={currentError.message} />
	{:else if currentError.code === 403}
		<PubPage40x code="403" description={currentError.message} />
	{:else if currentError.code === 410}
		<OptimizerPubPageFinished {currentError} />
	{:else}
		<PubPage404 description={currentError.message} />
	{/if}
{:else}
	<VehicleCategoriesLoader />
	<MetaBroker>
		<Router {url}>
			{#if !production}
				<Route path="/dev">
					<DefaultLandingPage />
					<FooterMM />
				</Route>
			{/if}
			<!-- route all urls that are not handled below to an iframe and show sirius UI -->

			<Route component={NestedSiriusIFramePage} />
			<Route path="/404" component={PubPage404} description={$t('UI.404.desc')} />

			<Route path="/401" component={PubPage40x} description={$t('UI.401.desc')} />

			<Route path="/saas-dashboard" component={AiDashboardSaasPage} />
			<Route path="/analysis" component={AnalyticalPage} />
			<Route path="/analytic" component={AnalyticalPage} />
			<Route path="/catalogs" component={MaterialCatalogPageManager} />
			<Route path="/catalog/:catalogName/material/:materialId">
				{#snippet children(params)}
					{#if params.catalogName && params.materialId}
						<MaterialCatalogPageManager
							catalogName={params.catalogName}
							materialId={params.materialId}
						/>
					{/if}
				{/snippet}
			</Route>
			<Route path="/corporateDesign" component={CorporateDesignPage} />
			<Route path="/dashboard" component={Dashboard} />
			<Route path="/hlpConfiguration" component={HelpConfiguration} />
			<Route path="/hlp" component={HelpBox} />
			<Route path="/installationCases" component={InstallationCasesPage} />
			<Route path="/mailProtocol" component={MailProtocolPage} />
			<Route path="/mailTemplates" component={MailTemplateConfigPage} />
			<Route path="/manage/tags" component={TagsConfigurationPage} />
			<Route path="/map" component={MapView} />
			<Route
				path="/materialProperties"
				component={MaterialCatalogPageManager}
				view={MaterialView.MATERIAL_PROPERTIES}
			/>
			<Route path="/materialProperty/:materialPropertyId">
				{#snippet children(params)}
					{#if params.materialPropertyId}
						<MaterialCatalogPageManager
							view={MaterialView.MATERIAL_PROPERTIES}
							materialPropertyId={params.materialPropertyId}
						/>
					{/if}
				{/snippet}
			</Route>
			<Route
				path="/materialSubstitutes"
				component={MaterialCatalogPageManager}
				view={MaterialView.MATERIAL_SUBSTITUTES}
			/>
			<Route path="/system/matching" component={MatchCheck} />
			<Route path="/ui/lot/:lotId/accountingMailConfiguration">
				{#snippet children(params)}
					{#if params.lotId}
						<LotAccountingMailConfigurationPage lotId={params.lotId} />
					{/if}
				{/snippet}
			</Route>
			<Route
				path="/angebot/:requestPublicId/requested/:requestedEntityType/:requestedEntityId/user/:offerUserPublicId"
			>
				{#snippet children(params)}
					{#if params.requestPublicId && params.requestedEntityType && params.requestedEntityId && params.offerUserPublicId}
						<OptimizerPageSaas
							requestPublicId={params.requestPublicId}
							requestedEntityType={params.requestedEntityType as RequestedEntityType}
							requestedEntityId={params.requestedEntityId}
							offerUserPublicId={params.offerUserPublicId}
						/>
					{/if}
				{/snippet}
			</Route>
			<!-- TODO qr /angebot/quotePublicId deleted -> equivalent necessary ?? -->
			<Route
				path="/lots/bid/:requestPublicId/requested/:requestedEntityType/:requestedEntityId/user/:offerUserPublicId"
			>
				{#snippet children(params)}
					{#if params.requestPublicId && params.requestedEntityType && params.requestedEntityId && params.offerUserPublicId}
						<OptimizerPage
							requestPublicId={params.requestPublicId}
							requestedEntityType={params.requestedEntityType as RequestedEntityType}
							requestedEntityId={params.requestedEntityId}
							offerUserPublicId={params.offerUserPublicId}
						/>
					{/if}
				{/snippet}
			</Route>
			<Route path="/lots/won/:lotId">
				{#snippet children(params)}
					{#if params.lotId}
						<OptimizerPage lotPublicId={params.lotId} />
					{/if}
				{/snippet}</Route
			>
			<Route path="/position/:lotPublicId">
				{#snippet children(params)}
					{#if params.lotPublicId}<OffererPubPage lotPublicId={params.lotPublicId} />
					{/if}
				{/snippet}
			</Route>
			<Route path="/position/:lotPublicId/gegenangebot/:reqPublicId/user/:offerUserPublicId">
				{#snippet children(params)}
					{#if params.lotPublicId && params.reqPublicId && params.offerUserPublicId}
						<OffererPubPage
							lotPublicId={params.lotPublicId}
							requestPublicId={params.reqPublicId}
							offerUserPublicId={params.offerUserPublicId}
						/>{/if}
				{/snippet}
			</Route>
		</Router>
	</MetaBroker>
{/if}
